<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";

const logger = useLogger("RankedReport");

/** 1) Expect 'id' from the parent */
const props = defineProps<{ id: string }>();

/** 2) Get access to the store's read method only */
const { getContentById } = useContentManager();

/**
 * 3) Read-only computed property wrapping the container definition data.
 */
const definitionData = computed(() => getContentById(props.id));


/** 6) Commands for context menu (optional) */
const commands = [
  {
    id: "editors-group",
    name: "Report",
    description: "Configure this report",
    type: "group",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Number of columns",
        action: async () => {
          logger.info("Edit command triggered for report", props.id);
          logger.debug("Emitting event for onEdit with report data", {
            data: definitionData.value,
          });
          // Casting event name to satisfy strict typing of event bus
          eventBus.emit(`component:onEdit:${props.id}` as any, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 7) Expose commands & properties for the resource editor / context menu */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Competency Descriptions",
    icon: "lucide:rocket",
  },
});

// Route (only needed when falling back to store-driven fetch)
const route = useRoute();

// Reports store (fallback) - rename loading to storeLoading for clarity
const { reportData, loading: storeLoading, error, fetchReports } = useReportsStore();

// Prefer data passed down from ReportProvider; includes `data` and `kind`.
const report = inject<{ data: any[]; kind?: string } | null>("report", null);

// Conditionally fetch reports only if we are NOT under a provider
onMounted(async () => {
  if (!report) {
    logger.info("Fetching ranked users from API (store fallback)");
    await fetchReports(route.query.user as string);
  }
});

// Compute current user (provider first, then store fallback)
const user = computed(() => {
  if (report && report.kind === "users" && Array.isArray(report.data)) {
    return report.data[0] ?? null;
  }
  // Fallback: first element from store's reportData
  return Array.isArray(reportData.value) ? reportData.value[0] : null;
});

// Derive & sort constructs (provider constructs > user.constructs > empty)
const sortedConstructs = computed(() => {
  let constructs: any[] = [];

  // 1) Provider delivers constructs directly
  if (report && report.kind === "constructs" && Array.isArray(report.data)) {
    constructs = report.data.slice();
  } else {
    // 2) Provider (or store) gives a user with constructs
    const u: any = user.value;
    if (u && Array.isArray(u.constructs)) {
      constructs = u.constructs.slice();
    }
  }

  // Optional filter by selectedConstructIds (if configured)
  const selectedIds: string[] = (definitionData.value as any)?.selectedConstructIds ?? [];
  if (selectedIds.length) {
    constructs = constructs.filter((c) => selectedIds.includes(String(c.id)));
  }

  return constructs.sort((a, b) => b.score - a.score);
});

interface ConstructLike { id?: string | number; name?: string; description?: string; score: number; [k: string]: any }
const getConstructIcon = (construct: ConstructLike): string => {
  if (construct.score >= 0 && construct.score < 0.35) return "lucide:trending-down";
  if (construct.score >= 0.35 && construct.score < 0.7) return "lucide:trending-up-down";
  if (construct.score >= 0.7) return "lucide:trending-up";
  return "lucide:circle";
};

// Color accessor (same pattern as stacked-bar). Settings key: colorAccessor
const colorAccessorCode = computed(() => (definitionData.value as any)?.colorAccessor ?? null);
const colorAccessor = computed<((d: any, i?: number) => string | null) | null>(() => {
  if (!colorAccessorCode.value) return null;
  try {
    // eslint-disable-next-line no-eval
    return eval(colorAccessorCode.value);
  } catch (err) {
    console.error("Error evaluating color accessor (competency descriptions):", err);
    return null;
  }
});

// Icon accessor (returns icon name string) Settings key: iconAccessor
const iconAccessorCode = computed(() => (definitionData.value as any)?.iconAccessor ?? null);
const iconAccessor = computed<((d: any, i?: number) => string | null) | null>(() => {
  if (!iconAccessorCode.value) return null;
  try {
    // eslint-disable-next-line no-eval
    return eval(iconAccessorCode.value);
  } catch (err) {
    console.error("Error evaluating icon accessor (competency descriptions):", err);
    return null;
  }
});

function resolveColor(d: any, i?: number): string {
  try {
    const fn = colorAccessor.value;
    const c = fn ? fn(d, i) : null;
    return (c && String(c).trim()) || '#000'; // black fallback only
  } catch (e) {
    return '#000';
  }
}

function resolveIcon(d: any, i?: number): string {
  try {
    const fn = iconAccessor.value;
    const iconName = fn ? fn(d, i) : null;
    if (iconName && typeof iconName === 'string' && iconName.trim().length) return iconName.trim();
  } catch {
    // ignore
  }
  return getConstructIcon(d); // fallback to previous heuristic
}

function colorBinding(d: any) {
  const c = resolveColor(d);
  if (/^text-[a-z0-9:-]+$/i.test(c)) return { class: c };
  if (/^(#|var\(|rgb|hsl)/i.test(c)) return { style: `color: ${c}` };
  return { style: `color: ${c}` };
}
</script>

<template>
  <div v-if="storeLoading && !report" class="text-center">
    <Icon mode="svg"
      name="lucide:loader-circle"
      class="mx-auto text-5xl text-primary animate-spin"
    />
    <h3 class="mt-2 text-sm font-semibold text-foreground">
      We're getting things ready
    </h3>
    <p class="mt-1 text-sm text-muted-foreground">
      Please be patient as we're loading some data for you...
    </p>
  </div>
  <div v-else class="space-y-6">
    <div
      v-for="construct in sortedConstructs"
      :key="construct.id"
      class="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2 tablet:grid-cols-[auto_minmax(0,1fr)_auto] tablet:gap-x-4"
    >
      <Icon
        mode="svg"
        :name="resolveIcon(construct) || 'lucide:circle'"
        v-bind="colorBinding(construct)"
        class="mt-0.5 size-6 shrink-0 tablet:size-8"
      />
      <div class="min-w-0">
        <h3 class="text-lg font-semibold leading-tight">{{ construct.name }}</h3>
        <p class="mt-1 text-sm leading-relaxed text-muted-foreground">
          {{ construct.description }}
        </p>
      </div>
      <span
        class="col-start-2 whitespace-nowrap text-xl font-bold tabular-nums tablet:col-start-3 tablet:row-start-1 tablet:text-2xl"
      >
        {{ construct.score.toFixed(2) }}%
      </span>
    </div>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/reports/individual/competency-descriptions.vue"),
  componentId: ComponentDefinitionIds.COMPETENCY_DESCRIPTIONS_REPORT,
  icon: "lucide:rocket",
  type: "Competency Decriptions",
  description: "Detailed descriptions of competencies.",
  default: {},
  categories: ["Reports"],
  order: 12005,
};
</script>
